import ClickableListItem from 'LuxKit/List/ClickableListItem'
import ListItemPrice from 'LuxKit/List/ListItemPrice'
import { OfferPageActions, takeOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import React, { useCallback, useId } from 'react'
import { getMultiRoomDurationOptions } from 'selectors/offerPage/multipleRoomBookingSelectors'
import RadioButton from 'LuxKit/Radio/RadioButton'
import { OfferDurationOption } from '../DurationTabs/OfferDurationTabs'
import noop from 'lib/function/noop'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import Label from 'LuxKit/Label'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import Pill from 'LuxKit/Pill'
import { isLPCTacticalEnabled } from 'lib/offer/offerUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import { getDurationOptions } from 'selectors/offerPage/offerPageSelectors'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'

export function getOptionLabel(option: OfferDurationOption, roomCount: number = 1, saleUnit: string = 'room') {
  if (option.soldOut) {
    return 'Sold out'
  } else if (option.lowestPrice === undefined) {
    return undefined
  } else {
    if (roomCount > 1) {
      return pluralizeToString(saleUnit, roomCount) + ' from'
    }
    return 'From'
  }
}

export function getSubtitleLabel(option: OfferDurationOption) {
  if (isLPCTacticalEnabled() && option.hasTactical) {
    return <LabelGroup>
      <Label
        startIcon={<SolidFireIcon colour="urgency" size="XS" />}
        variant="critical"
        kind="plain"
      >
        Limited Time Special
      </Label>
      {(option.discountPercent ?? 0) > 0 && <Pill kind="primary" variant="success">-{option.discountPercent}%</Pill>}
    </LabelGroup>
  }

  return undefined
}

interface Props {
  durationOptions: Array<OfferDurationOption>
  selectedDuration: number
  onClose?: () => void
  bundledWithFlightsOnly: boolean
  currentOfferId?: string
  bundleOfferId?: string
  onSelect?: (duration: number) => void
  rooms?: Array<App.RoomOccupants>
  saleUnit: string
  displayMemberPrice?: boolean
}

function OfferDurationOptions(props: Props) {
  const {
    durationOptions,
    onClose,
    selectedDuration,
    bundledWithFlightsOnly,
    currentOfferId,
    bundleOfferId,
    rooms,
    saleUnit,
    onSelect = noop,
    displayMemberPrice,
  } = props
  const dispatch = useOfferPageDispatch()

  const name = useId()

  const onDurationClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const duration = parseInt(e.currentTarget.dataset.duration!, 10)
    dispatch({ type: OfferPageActions.setDuration, duration, offerId: currentOfferId })
    onClose?.()
    onSelect(duration)
  }, [dispatch, currentOfferId, onClose, onSelect])
  return <>
    {durationOptions.map(option => {
      const price = option.lowestPrice
      const roundedLowestPrice = price ? Math.ceil(price) : price
      return <ClickableListItem
        key={option.duration}
        title={pluralizeToString('night', option.duration)}
        subtitle={getSubtitleLabel(option)}
        data-duration={option.duration}
        onClick={onDurationClick}
        startIcon={<RadioButton name={name} value={option.duration} checked={option.duration === selectedDuration} />}
        disabled={option.soldOut}
      >
        <ListItemPrice
          price={(option.soldOut || bundledWithFlightsOnly || bundleOfferId) ? undefined : roundedLowestPrice}
          label={bundledWithFlightsOnly || bundleOfferId ? undefined : getOptionLabel(option, rooms ? rooms.length : 1, saleUnit)}
          priceColour={displayMemberPrice ? 'lux-plus' : undefined}
        />
      </ClickableListItem> },
    )}
  </>
}

export default withOfferPageAndReduxState((state, appState) => {
  const currentOfferId = state.paginator?.currentOfferId
  const currentState = takeOfferState(state, currentOfferId)
  const isMultiRoom = isMultipleRoomBookingActive(state, appState)

  return {
    selectedDuration: currentState.duration,
    bundleOfferId: currentState.bundleOffer?.id,
    currentOfferId,
    displayMemberPrice: state.displayMemberPrice,
    durationOptions: isMultiRoom ? getMultiRoomDurationOptions(currentState, appState) : getDurationOptions(state, appState),
    bundledWithFlightsOnly: currentState.offer?.bundledWithFlightsOnly,
    rooms: isMultiRoom ? state.rooms : undefined,
    saleUnit: state.offer?.saleUnit ?? 'room',
  }
}, OfferDurationOptions)
